import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Icone optionnelle posee a gauche du champ (variante recherche).
   * Typiquement `<IconSearch />`. Rendue en `text-ink-300` et non interactive.
   */
  leftIcon?: ReactNode;
}

/**
 * Champ texte MyTaki. Theme via les design tokens Taki.
 *
 * Repos : h 50px, rayon 13px, surface-1, bordure border, texte ink-900,
 * placeholder ink-300. Hover : bordure border-strong. Focus : bordure
 * accent-border + halo lime 3px. Optionnellement une `leftIcon` (recherche).
 *
 * @example
 * <Input placeholder="Email" />
 * <Input leftIcon={<IconSearch />} placeholder="Rechercher une app" />
 */
const fieldClasses =
  'h-[3.125rem] w-full rounded-[0.8125rem] border border-border bg-surface-1 text-sm text-ink-900 ' +
  'font-sans transition-colors placeholder:text-ink-300 ' +
  'hover:border-border-strong ' +
  'focus-visible:outline-none focus-visible:border-accent-border ' +
  'focus-visible:shadow-ring-soft ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, type = 'text', leftIcon, ...props },
  ref,
) {
  if (leftIcon) {
    return (
      <div
        className={cn(
          'group relative flex h-[3.125rem] w-full items-center rounded-[0.8125rem] border border-border bg-surface-1',
          'transition-colors hover:border-border-strong',
          'focus-within:border-accent-border focus-within:shadow-ring-soft',
          className,
        )}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none flex shrink-0 items-center pl-4 text-ink-300"
        >
          {leftIcon}
        </span>
        <input
          ref={ref}
          type={type}
          className={cn(
            'h-full w-full rounded-[0.8125rem] bg-transparent pl-2.5 pr-4 text-sm text-ink-900',
            'font-sans placeholder:text-ink-300 focus-visible:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          {...props}
        />
      </div>
    );
  }

  return <input ref={ref} type={type} className={cn(fieldClasses, 'px-4', className)} {...props} />;
});
